import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import Checkbox from 'components/checkbox';
import { CountrySelectInput } from 'components/country-select-input';
import { FloatingLabelInput } from 'components/floating-label-fields';
import IconAmex from 'components/icons/amex';
import IconDinersClub from 'components/icons/diners-club';
import IconDiscover from 'components/icons/discover';
import IconJCB from 'components/icons/jcb';
import IconMastercard from 'components/icons/mastercard';
import IconVisa from 'components/icons/visa';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { FormValidationState } from 'utils/form';
import { creditCardNumberMask, expiryMask } from 'utils/masks';
import { IAdyenPaymentState, IPaymentErrors, IPaymentState, excludeNumeric } from 'utils/payment';

import AdyenCustomCardInputs from './adyen-card-form-inputs/adyen-custom-card-inputs';
import { IAdyenErrors, IAdyenState } from './adyen-card-form-inputs/types';
import { BillingSameAsDeliveryLabel, SaveCardForFutureLabel } from './credit-card-form-labels';
import FormTitle from './credit-card-form-title';
import { messages } from './messages';

interface IFormContainer {
  withBorder: boolean;
}

export const FormContainer = styled.div<IFormContainer>`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  background-color: transparent;
  font-size: 0.75rem;
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }

  ${Styles.mobile`
    margin-top: 1rem;
  `}

  ${Styles.desktop`
    padding: 1rem;

    ${(p: IFormContainer) =>
      p.withBorder &&
      css`
        border: 1px solid ${Styles.color.grey.four};
        background-color: white;
      `}
    `}
`;

export const NameOnCardWrapper = styled.div`
  flex-basis: 100%;
`;

export const CountrySelectWrapper = styled.div`
  flex-basis: 100%;
`;

export const CardNumberWrapper = styled.div`
  flex-basis: 100%;
  flex-grow: 1;
  position: relative;
  ${Styles.desktop`
    flex-basis: 50%;
  `}
`;

interface ICardValidationIndicator {
  isValid: boolean;
}

const CvvWrapper = styled.div`
  flex-basis: 40%;
  margin-left: 2.5%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const ExpiryWrapper = styled.div`
  flex-basis: 40%;
  margin-right: 2.5%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const StreetAddressWrapper = styled.div`
  flex-basis: 100%;
`;

const BillingAptWrapper = styled.div`
  flex-basis: 45%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 19%;
    margin-right: 5%;
  `}
`;
const BillingZipWrapper = styled.div`
  flex-basis: 45%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const BillingCityWrapper = styled.div`
  flex-basis: 60%;
  margin-right: 5%;
  ${Styles.desktop`
    flex-basis: 30%;
    margin-right: 5%;
  `}
`;

const BillingStateWrapper = styled.div`
  flex-basis: 35%;
  ${Styles.desktop`
    flex-basis: 16%;
    margin-right: 5%;
  `}
`;

const ExpiryCVVWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const AcceptedCards = styled.div`
  position: absolute;
  right: 1rem;
  top: 1.6rem;
  z-index: ${Styles.zIndex.max - 300};

  svg {
    height: 1.6rem;
    width: 1.6rem;
    margin-right: 0.5rem;
  }

  > :last-child {
    margin-right: 0;
  }
`;

interface ICreditCardFormInputs {
  errors: IPaymentErrors;
  isDelivery?: boolean;
  onChange: (name: string, value: string) => void;
  onAdyenChange?: (state: IAdyenState) => void;
  onAdyenError?: (errors: IAdyenErrors) => void;
  paymentValues: IPaymentState | IAdyenPaymentState;
  showSaveCard?: boolean;
  withBorder?: boolean;
  ariaLabel?: string;
  supportedCardTypes?: CardType[];
}

const CreditCardIcon: FC<{ cardType: string }> = ({ cardType }) =>
  ({
    AMEX: <IconAmex />,
    DISCOVER: <IconDiscover />,
    DINERS_CLUB: <IconDinersClub />,
    JCB: <IconJCB />,
    MASTERCARD: <IconMastercard />,
    VISA: <IconVisa />
  }[cardType] || null);

const CreditCardFormInputs: React.FC<ICreditCardFormInputs> = ({
  errors,
  isDelivery = false,
  onChange,
  onAdyenChange,
  onAdyenError,
  paymentValues,
  showSaveCard = true,
  withBorder = false,
  ariaLabel,
  supportedCardTypes = []
}) => {
  // retrieve additional credit card form props
  const { postalCodeInputProps, regionInputProps } = useCreditCardFormProps(
    paymentValues.billingCountry
  );
  const { formatMessage } = useIntl();
  const { isAdyen } = usePaymentContext();

  const handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement> = e =>
    onChange(e.target.name, e.target.value);

  // Hides Apt and State/Province field from Add New Payment Method form
  const shouldHideField = useFlag(LaunchDarklyFlag.ENABLE_HIDE_ADD_NEW_PAYMENT_FIELDS);

  return (
    <FormContainer withBorder={withBorder} aria-label={ariaLabel}>
      <FormTitle
        title={formatMessage(messages.pleaseEnterYourCreditCardInformation)}
        creditText={formatMessage(messages.credit)}
      />
      <NameOnCardWrapper>
        <FloatingLabelInput
          label={formatMessage(messages.nameOnCard)}
          name="nameOnCard"
          data-testid="nameOnCard"
          value={paymentValues.nameOnCard}
          onChange={handleChange}
          required
          autocomplete="cc-name"
          validation={errors.nameOnCard ? FormValidationState.INVALID : undefined}
          message={errors.nameOnCard}
        />
      </NameOnCardWrapper>
      {isAdyen ? (
        <AdyenCustomCardInputs onChange={onAdyenChange} onError={onAdyenError} />
      ) : (
        <>
          <CardNumberWrapper>
            <AcceptedCards>
              {paymentValues.isCardNumberValid ? (
                <CreditCardIcon cardType={paymentValues.cardType} />
              ) : (
                <>
                  {supportedCardTypes.map(
                    // @TODO: Global team to uncomment in upcoming task
                    () => null // <CreditCardIcon key={cardType} cardType={cardType} />
                  )}
                </>
              )}
            </AcceptedCards>
            <FloatingLabelInput
              label={formatMessage(messages.creditCardNumber)}
              name="cardNumber"
              data-testid="cardNumber"
              type="text"
              inputMode="numeric"
              value={paymentValues.cardNumber}
              onChange={handleChange}
              autocomplete="cc-number"
              validation={
                errors.cardType || errors.cardNumber ? FormValidationState.INVALID : undefined
              }
              message={errors.cardType || errors.cardNumber}
              required
              mask={creditCardNumberMask}
              guide
              placeholderChar=" "
            />
          </CardNumberWrapper>
          <ExpiryCVVWrapper>
            <ExpiryWrapper>
              <FloatingLabelInput
                label={formatMessage(messages.monthYearShort)}
                name="expiry"
                data-testid="expiry"
                aria-label={formatMessage(messages.cardExpiration)}
                type="text"
                inputMode="numeric"
                value={paymentValues.expiry}
                onChange={handleChange}
                required
                autocomplete="cc-exp"
                validation={errors.expiry ? FormValidationState.INVALID : undefined}
                message={errors.expiry}
                mask={expiryMask}
                guide={false}
              />
            </ExpiryWrapper>
            <CvvWrapper>
              <FloatingLabelInput
                label={formatMessage(messages.cvv)}
                name="cvv"
                data-testid="cvv"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={paymentValues.cvv}
                onChange={handleChange}
                maxLength="4"
                required
                autocomplete="cc-csc"
                validation={errors.cvv ? FormValidationState.INVALID : undefined}
                message={errors.cvv}
              />
            </CvvWrapper>
          </ExpiryCVVWrapper>
        </>
      )}
      {isDelivery && (
        <Checkbox
          data-testid="billing-same-as-delivery"
          label={<BillingSameAsDeliveryLabel />}
          name="billingAddressSameAsDelivery"
          checked={paymentValues.billingAddressSameAsDelivery}
          onChange={handleChange}
        />
      )}
      <StreetAddressWrapper>
        <FloatingLabelInput
          label={formatMessage(messages.streetAddress)}
          name="billingStreetAddress"
          data-testid="billingStreetAddress"
          value={paymentValues.billingStreetAddress}
          onChange={handleChange}
          required
          autocomplete="billing street-address"
          validation={errors.billingStreetAddress ? FormValidationState.INVALID : undefined}
          message={errors.billingStreetAddress}
          readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
        />
      </StreetAddressWrapper>
      {!shouldHideField && (
        <BillingAptWrapper>
          <FloatingLabelInput
            label={formatMessage(messages.aptOrSuite)}
            aria-label={formatMessage(messages.apartment)}
            name="billingApt"
            data-testid="billingApt"
            value={paymentValues.billingApt}
            onChange={handleChange}
            validation={errors.billingApt ? FormValidationState.INVALID : undefined}
            message={errors.billingApt}
            autocomplete="billing street-apt"
            readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
          />
        </BillingAptWrapper>
      )}
      <BillingCityWrapper>
        <FloatingLabelInput
          label={formatMessage(messages.city)}
          name="billingCity"
          data-testid="billingCity"
          value={paymentValues.billingCity}
          onChange={handleChange}
          required
          type="text"
          autocomplete="billing locality"
          validation={errors.billingCity ? FormValidationState.INVALID : undefined}
          message={errors.billingCity}
          readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
        />
      </BillingCityWrapper>
      {!shouldHideField && (
        <BillingStateWrapper>
          <FloatingLabelInput
            name="billingState"
            data-testid="billingState"
            value={excludeNumeric(paymentValues.billingState)}
            onChange={handleChange}
            required
            autocomplete="billing region"
            validation={errors.billingState ? FormValidationState.INVALID : undefined}
            message={errors.billingState}
            readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
            {...regionInputProps}
          />
        </BillingStateWrapper>
      )}
      <BillingZipWrapper>
        {/* Additional props are spread: label, maxLength, pattern */}
        <FloatingLabelInput
          name="billingZip"
          data-testid="billingZip"
          value={paymentValues.billingZip}
          onChange={handleChange}
          required
          autocomplete="billing postal-code"
          validation={errors.billingZip ? FormValidationState.INVALID : undefined}
          message={errors.billingZip}
          readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
          {...postalCodeInputProps}
        />
      </BillingZipWrapper>
      <CountrySelectWrapper>
        <CountrySelectInput
          label={formatMessage(messages.country)}
          name="billingCountry"
          data-testid="billingCountry"
          required
          value={paymentValues.billingCountry}
          onChange={handleChange}
          autocomplete="billing country"
          validation={errors.billingCountry ? FormValidationState.INVALID : undefined}
          message={errors.billingCountry}
          readOnly={!!(isDelivery && paymentValues.billingCountry)}
        />
      </CountrySelectWrapper>
      {showSaveCard && (
        <Checkbox
          label={<SaveCardForFutureLabel />}
          name="saveCard"
          data-testid="saveCard"
          checked={paymentValues.saveCard}
          onChange={handleChange}
        />
      )}
    </FormContainer>
  );
};

export default CreditCardFormInputs;
